<template>
  <div class="profile-settings-page d-flex flex-column min-vh-100">
    <!-- Header Navigation -->
    <Header />

    <!-- Main Content Area -->
    <main class="profile-settings-main flex-grow-1 py-5 animate-fade-in">
      <div class="container">
        <!-- Breadcrumb navigation -->
        <div class="row mb-4">
          <div class="col-12 text-start">
            <h2 class="h3 fw-bold font-serif text-primary-color m-0">Thiết lập tài khoản</h2>
            <p class="text-muted fs-7 m-0">Quản lý thông tin liên hệ, địa chỉ giao nhận và sở thích tìm kiếm</p>
          </div>
        </div>

        <div class="row g-4 text-start">
          <!-- Left Column: User Profile Badge & Sidebar Menu -->
          <div class="col-lg-4">
            <div class="profile-sidebar-card p-4 text-center sticky-top" style="top: 88px;">
              <div class="profile-avatar-section mb-4">
                <div class="avatar-glow-wrapper mx-auto mb-2">
                  <div class="avatar-circle-large">
                    <img v-if="userAvatar" :src="userAvatar" alt="Avatar" class="avatar-img-large" referrerpolicy="no-referrer" />
                    <icon v-else :icon="activeMenuIcon" class="avatar-icon-large" :class="{ 'text-warning-gold': $route.path.includes('preferences') }" />
                  </div>
                  <input ref="avatarFileInput" type="file" accept="image/*" class="d-none" @change="onAvatarSelected" />
                  <div class="glow-effect-large"></div>
                </div>
                <div class="avatar-actions d-flex justify-content-center gap-2 mb-2">
                  <button type="button" class="avatar-action-btn" :disabled="isUploadingAvatar" @click="triggerAvatarUpload">
                    <icon icon="fa-solid fa-camera" class="me-1" />Đổi ảnh
                  </button>
                  <button v-if="userAvatar" type="button" class="avatar-action-btn avatar-action-btn-danger" :disabled="isUploadingAvatar" @click="showDeleteAvatarModal = true">
                    <icon icon="fa-solid fa-trash-can" class="me-1" />Xóa ảnh
                  </button>
                </div>
                <h3 class="h5 fw-bold font-serif text-primary-color mb-1">{{ accountName || 'Thành viên Lối Đọc' }}</h3>
                <span class="badge bg-gold-badge px-3 py-1 text-xs text-white">
                  {{ accountRole === 'ROLE_ADMIN' ? 'Quản trị viên' : 'Thành viên' }}
                </span>
              </div>

              <!-- Settings Links list -->
              <div class="profile-nav-menu">
                <router-link 
                  to="/settings/profile" 
                  class="profile-nav-item text-decoration-none" 
                  active-class="active"
                >
                  <icon icon="fa-solid fa-user-pen" />
                  <span>Thông tin địa chỉ</span>
                </router-link>
                <router-link 
                  to="/settings/security" 
                  class="profile-nav-item text-decoration-none mt-2" 
                  active-class="active"
                >
                  <icon icon="fa-solid fa-shield-halved" />
                  <span>Bảo mật & Đăng nhập</span>
                </router-link>
                <router-link 
                  to="/settings/preferences" 
                  class="profile-nav-item text-decoration-none mt-2" 
                  active-class="active"
                >
                  <icon icon="fa-solid fa-wand-magic-sparkles" />
                  <span>Cá nhân hóa tìm kiếm</span>
                </router-link>
              </div>
            </div>
          </div>

          <!-- Right Column: Child Settings view -->
          <div class="col-lg-8">
            <div class="profile-form-card p-4">
              <router-view />
            </div>
          </div>
        </div>
      </div>
    </main>

    <!-- Footer -->
    <Footer />
  </div>

  <!-- Confirm Delete Avatar Modal -->
  <transition name="avatar-modal-fade">
    <div v-if="showDeleteAvatarModal" class="avatar-confirm-overlay" @click.self="showDeleteAvatarModal = false">
      <div class="avatar-confirm-box" role="dialog" aria-modal="true" aria-labelledby="deleteAvatarModalLabel">
        <div class="avatar-confirm-icon">
          <icon icon="fa-solid fa-trash-can" />
        </div>
        <h4 class="avatar-confirm-title" id="deleteAvatarModalLabel">Xóa ảnh đại diện</h4>
        <p class="avatar-confirm-text">Bạn có chắc muốn xóa ảnh đại diện hiện tại? Hệ thống sẽ sử dụng avatar mặc định.</p>
        <div class="avatar-confirm-actions">
          <button type="button" class="avatar-confirm-btn avatar-confirm-btn-cancel" :disabled="isDeletingAvatar" @click="showDeleteAvatarModal = false">Hủy</button>
          <button type="button" class="avatar-confirm-btn avatar-confirm-btn-danger" :disabled="isDeletingAvatar" @click="deleteAvatar">
            <icon v-if="isDeletingAvatar" icon="fa-solid fa-spinner" class="fa-spin me-1" />
            <span>{{ isDeletingAvatar ? 'Đang xóa...' : 'Xóa' }}</span>
          </button>
        </div>
      </div>
    </div>
  </transition>

  <!-- OAuth Avatar Source Modal -->
  <transition name="avatar-modal-fade">
    <div v-if="showOauthAvatarModal" class="avatar-confirm-overlay" @click.self="closeOauthAvatarModal">
      <div class="oauth-avatar-box" role="dialog" aria-modal="true" aria-labelledby="oauthAvatarModalLabel">
        <div class="oauth-avatar-head">
          <div class="avatar-confirm-icon">
            <icon icon="fa-solid fa-id-badge" />
          </div>
          <h4 class="avatar-confirm-title" id="oauthAvatarModalLabel">Chọn nguồn ảnh đại diện</h4>
          <p class="avatar-confirm-text">Bạn đã đăng nhập bằng Google, hãy chọn cách đặt ảnh đại diện của mình.</p>
        </div>

        <div class="oauth-avatar-options">
          <button type="button" class="oauth-avatar-option" :disabled="isApplyingOauthAvatar" @click="applyOauthAvatar">
            <span class="oauth-avatar-preview">
              <img v-if="oauthAvatar" :src="oauthAvatar" alt="Avatar Google" />
            </span>
            <span class="oauth-avatar-option-info">
              <strong>Ảnh giống với Google của bạn</strong>
              <small>Dùng ảnh đại diện từ tài khoản Google</small>
            </span>
            <icon v-if="isApplyingOauthAvatar" icon="fa-solid fa-spinner" class="fa-spin oauth-avatar-option-spinner" />
            <icon v-else icon="fa-solid fa-chevron-right" class="oauth-avatar-option-arrow" />
          </button>

          <button type="button" class="oauth-avatar-option" :disabled="isApplyingOauthAvatar" @click="pickLocalAvatar">
            <span class="oauth-avatar-option-icon">
              <icon icon="fa-solid fa-image" />
            </span>
            <span class="oauth-avatar-option-info">
              <strong>Chọn ảnh dưới máy</strong>
              <small>Tải lên ảnh từ thiết bị của bạn</small>
            </span>
            <icon icon="fa-solid fa-chevron-right" class="oauth-avatar-option-arrow" />
          </button>
        </div>

        <div class="oauth-avatar-actions">
          <button type="button" class="avatar-confirm-btn avatar-confirm-btn-cancel" :disabled="isApplyingOauthAvatar" @click="closeOauthAvatarModal">Hủy</button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script src="./settings-layout.js"></script>
<style scoped src="./settings-layout.css"></style>
